import { useGetChartingRecallSettings } from "@src/appV2/Charting/api/useGetChartingRecallSettings";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useIsDailyViewEnabled } from "@src/appV2/redesign/DailyView/hooks/useIsDailyViewEnabled";
import { useIsChatRedesignEnabled } from "@src/appV2/redesign/hooks/useIsChatRedesignEnabled";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { PlacementAccessControlStatus } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useIsInterviewAvailabilityMenuVisible } from "@src/appV2/InterviewSettings/useIsInterviewAvailabilityMenuVisible";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { evaluateWorkplaceToWorkplaceReferrals } from "@src/containers/workplaceReferAndEarn/utils";
import { useFacilityOnboardingFlag } from "@src/hooks/useFacilityOnboardingFlag";
import { type SessionType } from "@src/modules/interface";
import constate from "constate";
import { useFlags } from "launchdarkly-react-client-sdk";
import includes from "lodash/includes";
import { useSelector } from "react-redux";

import { hasAnyTrueValue } from "./utils";
import { useShowLiveMapView } from "@src/containers/app/hooks/showLiveMapView";
import { useShowContractTerms } from "@src/containers/app/hooks/showContractTerms";
import { isDefined } from "@clipboard-health/util-ts";
import { AnalyticsType } from "@src/appV2/Analytics/constants";

export interface WorkplaceMenuPrivileges {
  homeHealthSection?: {
    cases: boolean;
    visitVerification: boolean;
  };
  workplaceOnboarding?: boolean;
  dailyView?: boolean;
  calendar?: boolean;
  chat?: boolean;
  chatRedesign?: boolean;
  settingsRedesign?: boolean;
  myAccountRedesign?: boolean;
  professionalsSection?: {
    myProfessionals: boolean;
    browseProfessionals: boolean;
  };
  permanentHiringSection?: {
    placementsLandingPage: boolean;
    interviewSchedule: boolean;
    applicationAndJobs: boolean;
    browseTalentPool: boolean;
    interviewAvailability: boolean;
  };
  isPermanentHiringSectionLoading?: boolean;
  charting?: boolean;
  analyticsSection?: {
    workplaceAnalytics: boolean;
    corporateAnalytics: boolean;
  };
  corporateCenterSection?: {
    corporateAnalytics: boolean;
    spendingControls: boolean;
  };
  liveWorkerETA?: boolean;
  workplaceRulesQuiz?: boolean;
  managementSection?: {
    myAccount: boolean;
    teamMembers: boolean;
    requiredDocuments: boolean;
    workplaceInformation: boolean;
    settings: boolean;
    timeclockingSettings: boolean;
  };
  billingSection?: {
    reporting: boolean;
    upcomingCharges: boolean;
    invoiceHistory: boolean;
    contractTerms: boolean;
    workplaceSpendingControls: boolean;
  };
  helpCenter?: boolean;
  referAndEarn?: boolean;
  // Route authorization flags (not for menu visibility)
  routeAuth?: {
    isWorkplaceOrAdmin: boolean;
    hasPermissionToAccessWorkerDirectory: boolean;
    isDocumentAuthorized: boolean;
    hasDefaultWorkplacePermissions: boolean;
    isFacilityInvoiceDisputeAuthorized: boolean;
    isInvoiceViewAuthorized: boolean;
    workplaceSpendingControls: boolean;
  };
}

/* eslint-disable complexity */
export function useGetWorkplaceMenuPrivileges(): WorkplaceMenuPrivileges {
  const session = useSelector((state: SessionType) => state.session);
  const { user, profile, type } = session ?? {};
  const { access: roles, permissions } = user ?? {};

  const effectivePerms = useEffectivePermissions();
  const {
    hasAdminPrivileges,
    isCurrentlyAdmin,
    hasDefaultWorkplacePermissions: hasDefaultPerms,
    canViewWorkplaceChat,
    canAccessWorkerDirectory,
    canViewBilling,
    canEditDocuments,
    canViewInvoices,
    hasAdminRole,
  } = effectivePerms;

  const isAdmin = isCurrentlyAdmin;
  const isWorkplace = type === "FACILITY";

  const workplaceOnboarding = useFacilityOnboardingFlag({
    workplaceCreationDate: profile?.createdAt,
    roles: roles ?? [],
    isAdmin,
    isWorkplace,
  });

  // Use the value from our hook which correctly handles pseudo-login
  const hasDefaultWorkplacePermissions = hasDefaultPerms;

  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthHiddenMenuItemsEnabled = useCbhFlag(
    CbhFeatureFlag.HOME_HEALTH_MENU_ITEMS_HIDDEN,
    {
      defaultValue: true,
    }
  );

  const hideMenuItemsForHomeHealth =
    isHomeHealthEnabled && isHomeHealthHiddenMenuItemsEnabled && !hasAdminPrivileges;

  const isRecruitmentOnly =
    !hasAdminPrivileges &&
    includes(roles, FACILITY_ROLES.RECRUITMENT) &&
    !hasDefaultWorkplacePermissions;

  // Home Health specific menu items (hidden for recruiter-only users)
  const homeHealthCases = isHomeHealthEnabled && !isRecruitmentOnly;
  const homeHealthVisitVerification = isHomeHealthEnabled && !isRecruitmentOnly;
  const homeHealthSection = {
    cases: homeHealthCases,
    visitVerification: homeHealthVisitVerification,
  };

  const isDailyViewEnabled = useIsDailyViewEnabled();

  const dailyView =
    isDailyViewEnabled && hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  const calendar = hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  // Use the value from our hook which correctly handles permissions
  const chat = canViewWorkplaceChat;

  const isChatRedesignEnabled = useIsChatRedesignEnabled();
  const chatRedesign = canViewWorkplaceChat && isChatRedesignEnabled;

  const isSettingsRedesignEnabled = useCbhFlag(CbhFeatureFlag.SETTINGS_REDESIGN_ENABLED, {
    defaultValue: false,
  });

  const isMyAccountRedesignEnabled = useCbhFlag(CbhFeatureFlag.MY_ACCOUNT_REDESIGN_ENABLED, {
    defaultValue: false,
  });

  const myProfessionals = !hideMenuItemsForHomeHealth;

  const hasPermissionToAccessWorkerDirectory = canAccessWorkerDirectory;

  const ldFlags = useFlags();
  const hideBrowserWorkers = ldFlags?.[CbhFeatureFlag.HIDE_BROWSER_WORKERS];

  const browseProfessionals =
    hasPermissionToAccessWorkerDirectory && !isHomeHealthEnabled && !hideBrowserWorkers;

  const professionalsSection = { myProfessionals, browseProfessionals };

  const { isEnabled: isPlacementsEnabled, accessLevel: placementsAccessLevel } =
    useGetPlacementsAccessLevel();

  const isInterviewAvailabilityVisible = useIsInterviewAvailabilityMenuVisible();

  const placementsLandingPage = placementsAccessLevel === PlacementAccessControlStatus.LANDING_PAGE;

  const interviewSchedule = isPlacementsEnabled;

  const applicationAndJobs = isPlacementsEnabled;

  const browseTalentPool =
    isPlacementsEnabled && placementsAccessLevel !== PlacementAccessControlStatus.EDIT_FREE;

  const interviewAvailability = isPlacementsEnabled && isInterviewAvailabilityVisible;

  const hasPermissionToRecruit =
    hasAdminPrivileges ||
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);

  const { data: facilityProfileData, isInitialLoading: isInitialLoadingFacilityProfile } =
    useGetFacilityProfile(profile?.userId ?? "", "placementSettings.isProspect", {
      enabled: isWorkplace && isDefined(profile?.userId) && hasPermissionToRecruit,
    });
  const isProspect = facilityProfileData?.placementSettings?.isProspect ?? false;

  const permanentHiringSection = {
    placementsLandingPage,
    interviewSchedule,
    applicationAndJobs,
    browseTalentPool,
    interviewAvailability,
  };

  const { chartingRecallEnabled } = useGetChartingRecallSettings({
    workplaceId: profile?.userId,
    options: { enabled: isWorkplace },
  });

  const canAccessCharting =
    isAdmin ||
    !includes(roles, FACILITY_ROLES.DOCUMENTS) ||
    includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_CHARTING);

  const charting = chartingRecallEnabled && canAccessCharting && !isRecruitmentOnly;

  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });
  const hasAnalyticsPermissions =
    analyticsConfig.length > 0 && (hasAdminPrivileges || includes(roles, FACILITY_ROLES.ADMIN));
  const workplaceAnalytics = isDefined(
    analyticsConfig.find((e) => e.type === AnalyticsType.WORKPLACE_FLEX)
  );
  const corporateAnalytics = isDefined(
    analyticsConfig.find((e) => e.type === AnalyticsType.CORPORATE_FLEX)
  );
  const analyticsSection = { workplaceAnalytics, corporateAnalytics };

  // Corporate Center section (spending controls enabled)
  const spendingControlsConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS, {
    defaultValue: { enabled: false },
  });
  const isSpendingControlsEnabled = spendingControlsConfig.enabled;
  const isSpendingControlsInternalOnly =
    spendingControlsConfig.enabled && spendingControlsConfig.internalUseOnly;
  const hasCorporateAdminRole = includes(roles, FACILITY_ROLES.CORPORATE_ADMIN);
  const hasCorporateAnalyticsAccess = corporateAnalytics;
  // When internal-only, only Clipboard employees (admins) can access spending controls
  const canAccessCorporateSpendingControls = isSpendingControlsInternalOnly
    ? hasAdminPrivileges
    : hasAdminPrivileges || hasCorporateAdminRole;
  const shouldShowCorporateCenter =
    isSpendingControlsEnabled &&
    (canAccessCorporateSpendingControls || hasCorporateAnalyticsAccess);
  const corporateCenterSection = shouldShowCorporateCenter
    ? {
        corporateAnalytics: hasCorporateAnalyticsAccess,
        spendingControls: canAccessCorporateSpendingControls,
      }
    : undefined;

  const shouldShowLiveMapView = useShowLiveMapView();

  const liveWorkerETA =
    shouldShowLiveMapView && hasDefaultWorkplacePermissions && !isHomeHealthEnabled;

  const workplaceRulesQuiz =
    (ldFlags?.[CbhFeatureFlag.ROLLOUT_QUIZ_V2_FACILITY_RULES_AI] ?? false) && !isRecruitmentOnly;

  const myAccount = ldFlags?.[CbhFeatureFlag.MY_ACCOUNT_PAGE] && !hasAdminPrivileges;

  const teamMembers =
    hasAdminPrivileges ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.INVOICES);

  const workplaceInformation =
    !workplaceRulesQuiz && hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  const settings =
    (includes(roles, FACILITY_ROLES.ADMIN) || hasAdminPrivileges) && !hideMenuItemsForHomeHealth;

  const settingsRedesign = settings && isSettingsRedesignEnabled;

  const myAccountRedesign = myAccount && isMyAccountRedesignEnabled;

  const timeclockingSettings = includes(roles, FACILITY_ROLES.ADMIN) && !settings;

  const managementSection = {
    myAccount,
    teamMembers,
    requiredDocuments: true,
    workplaceInformation,
    settings,
    timeclockingSettings,
  };

  const hasBillingRights = canViewBilling;
  const reporting =
    (hasAdminPrivileges ||
      includes(roles, FACILITY_ROLES.ADMIN) ||
      includes(roles, FACILITY_ROLES.INVOICES) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT)) &&
    !hideMenuItemsForHomeHealth;

  // Admin users need specific employee roles that the backend /api/billing/upcoming-charges
  // endpoint requires. Without these roles, the API returns 403.
  const adminCanAccessUpcomingCharges =
    !hasAdminPrivileges ||
    hasAdminRole(EMPLOYEE_ROLES.ADMINISTRATIVE) ||
    hasAdminRole(EMPLOYEE_ROLES.BILLING) ||
    hasAdminRole(EMPLOYEE_ROLES.SHIFT_VERIFICATION);

  const upcomingCharges =
    adminCanAccessUpcomingCharges &&
    (hasBillingRights ||
      includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const invoiceHistory =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES);

  const shouldShowContractTerms = useShowContractTerms(false);

  const hasViewContractTermsRights =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS);

  const contractTerms =
    shouldShowContractTerms && hasViewContractTermsRights && !hideMenuItemsForHomeHealth;

  // Workplace spending controls: enabled for facility admins and staffing coordinators
  // who are NOT corporate admins (they use the Corporate Center page instead)
  const workplaceSpendingControls =
    isSpendingControlsEnabled &&
    !hasAdminPrivileges &&
    !hasCorporateAdminRole &&
    (includes(roles, FACILITY_ROLES.ADMIN) || includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const billingSection = {
    reporting,
    upcomingCharges,
    invoiceHistory,
    contractTerms,
    workplaceSpendingControls,
  };

  // Hide Help Center for Home Healthcare and Hospice workplace types
  const helpCenter = !isHomeHealthEnabled;

  const referAndEarn =
    !isHomeHealthEnabled &&
    evaluateWorkplaceToWorkplaceReferrals(ldFlags, user, isWorkplace, hasAdminPrivileges);

  // Route authorization (mirrors legacy appLayout.tsx checks)
  const isWorkplaceOrAdmin = isWorkplace || hasAdminPrivileges;

  const isDocumentAuthorized = canEditDocuments;

  // Facilities can dispute invoices if they have invoice permissions, but not if they're admins pseudo-logged in
  const isFacilityInvoiceDisputeAuthorized = isWorkplace && !hasAdminPrivileges && canViewInvoices;

  const isInvoiceViewAuthorized = canViewInvoices;

  const canAccessWorkplaceSpendingControls =
    isSpendingControlsEnabled &&
    !hasCorporateAdminRole &&
    (isAdmin ||
      includes(roles, FACILITY_ROLES.ADMIN) ||
      includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const routeAuth = {
    isWorkplaceOrAdmin,
    hasPermissionToAccessWorkerDirectory,
    isDocumentAuthorized: Boolean(isDocumentAuthorized),
    hasDefaultWorkplacePermissions,
    isFacilityInvoiceDisputeAuthorized: Boolean(isFacilityInvoiceDisputeAuthorized),
    isInvoiceViewAuthorized: Boolean(isInvoiceViewAuthorized),
    workplaceSpendingControls: canAccessWorkplaceSpendingControls,
  };

  if (!isWorkplace) {
    return { routeAuth };
  }

  return {
    homeHealthSection: isHomeHealthEnabled ? homeHealthSection : undefined,

    workplaceOnboarding: isHomeHealthEnabled ? false : workplaceOnboarding,
    dailyView,
    calendar,
    chat,
    chatRedesign,
    settingsRedesign,
    myAccountRedesign,

    professionalsSection:
      (user
        ? hasAdminPrivileges ||
          includes(user.access, FACILITY_ROLES.ADMIN) ||
          includes(user.access, FACILITY_ROLES.INVOICES) ||
          includes(user.access, FACILITY_ROLES.SHIFT_MANAGEMENT)
        : false) && hasAnyTrueValue(professionalsSection)
        ? professionalsSection
        : undefined,

    permanentHiringSection:
      hasPermissionToRecruit &&
      hasAnyTrueValue(permanentHiringSection) &&
      !(isProspect && !hasAdminPrivileges)
        ? permanentHiringSection
        : undefined,

    isPermanentHiringSectionLoading: isInitialLoadingFacilityProfile && !hasAdminPrivileges,

    charting,

    analyticsSection:
      (hasAnalyticsPermissions ?? hasAnyTrueValue(analyticsSection)) ? analyticsSection : undefined,

    corporateCenterSection,

    liveWorkerETA,

    workplaceRulesQuiz,

    managementSection: hasAnyTrueValue(managementSection) ? managementSection : undefined,

    billingSection: hasAnyTrueValue(billingSection) ? billingSection : undefined,

    helpCenter,
    referAndEarn,
    routeAuth,
  };
}
/* eslint-enable complexity */

export const [WorkplaceMenuPrivilegesProvider, useWorkplaceMenuPrivilegesProviderContext] =
  constate(useGetWorkplaceMenuPrivileges);
